import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarDaysIcon,
  TvIcon,
  TrashIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";
import { ScheduleBatchItem } from "../../schemas/scheduleSchema";

interface ScheduleDetailsModalProps {
  isOpen: boolean;
  schedule: ScheduleBatchItem;
  onClose: () => void;
  onDelete: (schedule: ScheduleBatchItem) => void;
  onDeleteSelectedScreens: (
    schedule: ScheduleBatchItem,
    screenIds: string[],
  ) => void;
}

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const formatTime = (time?: string) => {
  if (!time) return "All day";
  const parts = time.split(":");
  const hours = parseInt(parts[0]);
  const minutes = parts[1] || "00";
  const seconds = parts[2] || "00";
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes}:${seconds} ${ampm}`;
};

const ScheduleDetailsModal: React.FC<ScheduleDetailsModalProps> = ({
  isOpen,
  schedule,
  onClose,
  onDelete,
  onDeleteSelectedScreens,
}) => {
  const [selectedScreenIds, setSelectedScreenIds] = useState<string[]>([]);
  const [screenSearch, setScreenSearch] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setSelectedScreenIds([]);
      setScreenSearch("");
    }
  }, [isOpen, schedule.batchId]);

  const toggleScreenSelection = (screenId: string) => {
    setSelectedScreenIds((prev) =>
      prev.includes(screenId)
        ? prev.filter((id) => id !== screenId)
        : [...prev, screenId],
    );
  };

  const hasSelection = selectedScreenIds.length > 0;

  const screensForDisplay = useMemo(() => schedule.screens, [schedule.screens]);

  const filteredScreensForDisplay = useMemo(() => {
    const query = screenSearch.trim().toLowerCase();
    if (!query) {
      return screensForDisplay;
    }
    return screensForDisplay.filter((screen) =>
      screen.screenName.toLowerCase().includes(query),
    );
  }, [screensForDisplay, screenSearch]);

  const allSelectableScreenIds = useMemo(
    () => screensForDisplay.map((screen) => screen.id),
    [screensForDisplay],
  );

  const allSelected =
    allSelectableScreenIds.length > 0 &&
    allSelectableScreenIds.every((id) => selectedScreenIds.includes(id));

  const handleToggleSelectAll = () => {
    if (allSelected) {
      setSelectedScreenIds([]);
      return;
    }
    setSelectedScreenIds(allSelectableScreenIds);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl p-8 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-3 text-gray-500 hover:text-gray-800"
        >
          ✕
        </button>

        {/* Title */}
        <h2 className="text-xl font-semibold text-gray-800 mb-6">
          Schedule Details
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Overview Section */}
          <div className="border rounded-xl p-4 bg-gray-50 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <CalendarDaysIcon className="w-5 h-5 text-gray-600" />
              <span className="font-medium text-gray-700">Schedule Info</span>
            </div>
            <div className="space-y-3">
              <div>
                <span className="text-xs text-gray-500 block">Playlist</span>
                <span className="text-sm font-medium text-gray-800">
                  {schedule.playlistName}
                </span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">Date Range</span>
                <span className="text-sm text-gray-800">
                  {formatDate(schedule.startDate)} —{" "}
                  {formatDate(schedule.endDate)}
                </span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">Time Slot</span>
                <span className="text-sm text-gray-800">
                  {schedule.startTime
                    ? `${formatTime(schedule.startTime)} — ${formatTime(schedule.endTime)}`
                    : "All day"}
                </span>
              </div>
            </div>
          </div>

          {/* Days of Week Section */}
          <div className="border rounded-xl p-4 bg-gray-50 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <ListBulletIcon className="w-5 h-5 text-gray-600" />
              <span className="font-medium text-gray-700">Days of Week</span>
            </div>
            {schedule.daysOfWeek && schedule.daysOfWeek.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {[
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                  "Sunday",
                ].map((day) => {
                  const isActive = schedule.daysOfWeek?.some(
                    (d) => d.toLowerCase() === day.toLowerCase(),
                  );
                  return (
                    <span
                      key={day}
                      className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium ${
                        isActive
                          ? "bg-primary-default text-white"
                          : "bg-gray-200 text-gray-400"
                      }`}
                    >
                      {day.slice(0, 3)}
                    </span>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {[
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                  "Sunday",
                ].map((day) => (
                  <span
                    key={day}
                    className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-default text-white"
                  >
                    {day.slice(0, 3)}
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-400 mt-3">
              {schedule.daysOfWeek && schedule.daysOfWeek.length > 0
                ? `Active on ${schedule.daysOfWeek.length} day(s)`
                : "Active on all days"}
            </p>
          </div>

          {/* Screens Section */}
          <div className="border rounded-xl p-4 bg-gray-50 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <TvIcon className="w-5 h-5 text-gray-600" />
              <span className="font-medium text-gray-700">
                Assigned Screens ({screensForDisplay.length})
              </span>
            </div>
            <div className="space-y-2 mb-3">
              <input
                type="text"
                value={screenSearch}
                onChange={(e) => setScreenSearch(e.target.value)}
                placeholder="Search screens..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-default"
              />
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleToggleSelectAll}
                    disabled={allSelectableScreenIds.length === 0}
                    className="h-4 w-4 rounded border-gray-300 text-primary-default focus:ring-primary-default"
                  />
                  Select all
                </label>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[200px] space-y-2 pr-1">
              {filteredScreensForDisplay.length === 0 ? (
                <div className="text-sm text-gray-500 px-1 py-3">
                  No screens found for this search.
                </div>
              ) : (
                filteredScreensForDisplay.map((screen) => (
                  <div
                    key={screen.id}
                    className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border text-gray-600 bg-white border-gray-200 cursor-pointer"
                    onClick={() => toggleScreenSelection(screen.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedScreenIds.includes(screen.id)}
                      aria-label={`Select ${screen.screenName}`}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => toggleScreenSelection(screen.id)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-default focus:ring-primary-default"
                    />
                    <TvIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    {screen.screenName}
                  </div>
                ))
              )}
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Select one or more screens to remove this schedule only from those
              screens. Selected: {selectedScreenIds.length}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6">
          {hasSelection && (
            <button
              onClick={() => {
                onClose();
                onDeleteSelectedScreens(schedule, selectedScreenIds);
              }}
              className="px-4 py-2 bg-amber-600 hover:bg-warning-default text-white rounded-lg flex items-center gap-2"
            >
              <TrashIcon className="w-4 h-4" />
              Delete from Selected Screens
            </button>
          )}
          <button
            onClick={() => {
              onClose();
              onDelete(schedule);
            }}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2"
          >
            <TrashIcon className="w-4 h-4" />
            Delete Schedule
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleDetailsModal;
